/**
 * One télé-expertise dossier, full screen.
 *
 * It used to be a panel beside the queue, which made both cramped: the list could not shed anything
 * and the dossier could not gain anything. A dossier is read, and reading is what a whole screen is
 * for — the same choice the patient record made long ago, which is why this one is addressable too.
 * A surgeon can send a colleague the URL of a dossier.
 *
 * Its own route rather than a modal, and deliberately so: the compte rendu is written here, the
 * writing takes minutes, and a dialog that loses a draft to a stray click outside it is a dialog
 * that will eventually lose one.
 *
 * Two columns on a wide screen. The dossier on the left, because it is what the practitioner reads;
 * the compte rendu on the right, sticky, because it is what they write. On a narrow one the compte
 * rendu comes first — by then the reading has already happened.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams, type To } from "react-router";
import { ArrowLeft, Baby, ExternalLink, TimerReset, UserRound } from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";
import { toast } from "sonner";
import { CompteRenduPanel } from "@app/components/teleexpertise/CompteRenduPanel";
import { DossierDetail } from "@app/components/teleexpertise/DossierDetail";
import API from "@app/services/api";
import { cn } from "@app/lib/utils";
import {
  TAG,
  STATUT_LABELS,
  delaiStyle,
  dossierName,
  priorityStyle,
  statutStyle,
  type CompteRendu,
  type DossierDetail as Dossier,
  type QueueOrigin,
  type SuiteDonnee,
} from "@app/utils/teleexpertise";

/** Which long-running action is in flight, so exactly one spinner can be shown at a time. */
type Busy = "generate" | "save" | "validate" | "reopen" | null;

export default function TeleExpertiseDossierPage() {
  const { dossierId } = useParams();
  const { i18n, t } = useLingui();
  const location = useLocation();
  // Absent on a link a colleague sent: « Retour » then opens the queue as it opens by default.
  const origin = (location.state as { origin?: QueueOrigin } | null)?.origin;
  const backTo: To = { pathname: "/tele-expertise", search: origin?.search };

  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<Busy>(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    if (!dossierId) return;
    setLoading(true);
    const response = (await API.get({ path: `/teleexpertise/dossiers/${dossierId}` })) as {
      ok?: boolean;
      data?: Dossier;
      error?: string;
    };
    setLoading(false);
    if (!response?.ok || !response.data) {
      toast.error(response?.error || t`Impossible d'ouvrir ce dossier.`);
      return;
    }
    setDossier(response.data);
    setNote(response.data.triage_note ?? "");
  }, [dossierId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Fold a compte rendu answer back into the dossier without refetching the whole record. */
  const mergeCompteRendu = useCallback((compteRendu: CompteRendu, rest?: Partial<Dossier>) => {
    setDossier((previous) =>
      previous ? { ...previous, ...(rest ?? {}), compte_rendu: compteRendu } : previous,
    );
  }, []);

  const generate = useCallback(
    async (suite: SuiteDonnee) => {
      setBusy("generate");
      const response = (await API.post({
        path: `/teleexpertise/dossiers/${dossierId}/compte-rendu`,
        body: { suite_donnee: suite },
      })) as { ok?: boolean; data?: CompteRendu; error?: string };
      setBusy(null);
      if (!response?.ok || !response.data) {
        toast.error(response?.error || t`Le compte rendu n'a pas pu être rédigé.`);
        return;
      }
      mergeCompteRendu(response.data, { suite_donnee: suite });
      toast.success(t`Compte rendu rédigé. Relisez-le avant de valider.`);
    },
    [dossierId, mergeCompteRendu, t],
  );

  const save = useCallback(
    async (texte: string) => {
      setBusy("save");
      const response = (await API.patch({
        path: `/teleexpertise/dossiers/${dossierId}/compte-rendu`,
        body: { texte },
      })) as { ok?: boolean; data?: CompteRendu; error?: string };
      setBusy(null);
      if (!response?.ok || !response.data) {
        toast.error(response?.error || t`Le brouillon n'a pas pu être enregistré.`);
        return;
      }
      mergeCompteRendu(response.data);
    },
    [dossierId, mergeCompteRendu, t],
  );

  const validate = useCallback(
    async (suite: SuiteDonnee, texte: string, internalNote: string) => {
      setBusy("validate");
      const response = (await API.post({
        path: `/teleexpertise/dossiers/${dossierId}/compte-rendu/validation`,
        body: { suite_donnee: suite, texte, note: internalNote || null },
      })) as {
        ok?: boolean;
        data?: (Partial<Dossier> & { compte_rendu: CompteRendu }) | undefined;
        error?: string;
      };
      setBusy(null);
      if (!response?.ok || !response.data) {
        toast.error(response?.error || t`Le compte rendu n'a pas pu être validé.`);
        return;
      }
      const { compte_rendu: compteRendu, ...rest } = response.data;
      mergeCompteRendu(compteRendu, rest);
      toast.success(t`Compte rendu validé et signé.`);
    },
    [dossierId, mergeCompteRendu, t],
  );

  const reopen = useCallback(async () => {
    setBusy("reopen");
    const response = (await API.patch({
      path: `/teleexpertise/dossiers/${dossierId}/triage`,
      body: { statut: "recu", note: note || null },
    })) as { ok?: boolean; error?: string };
    setBusy(null);
    if (!response?.ok) {
      toast.error(response?.error || t`Le dossier n'a pas pu être rouvert.`);
      return;
    }
    // Refetched rather than patched in place: reopening changes the status, the suite and what the
    // panel is allowed to do, and guessing all three from here is how the two drift apart.
    await load();
  }, [dossierId, load, note, t]);

  const download = useCallback(
    async (brouillon: boolean) => {
      const response = (await API.get({
        path: `/teleexpertise/dossiers/${dossierId}/compte-rendu/pdf`,
        query: { brouillon: brouillon || undefined },
      })) as { ok?: boolean; data?: { url?: string }; error?: string };
      if (!response?.ok || !response.data?.url) {
        toast.error(response?.error || t`Le lien de téléchargement n'a pas pu être créé.`);
        return;
      }
      window.open(response.data.url, "_blank", "noopener");
    },
    [dossierId, t],
  );

  const downloadPiece = useCallback(
    async (documentId: string) => {
      const response = (await API.get({
        path: `/teleexpertise/dossiers/${dossierId}/pieces/${documentId}`,
      })) as { ok?: boolean; data?: { url?: string }; error?: string };
      if (!response?.ok || !response.data?.url) {
        toast.error(response?.error || t`Le lien de téléchargement n'a pas pu être créé.`);
        return;
      }
      window.open(response.data.url, "_blank", "noopener");
    },
    [dossierId, t],
  );

  if (loading && !dossier) {
    return (
      <p className="p-6 text-sm text-slate-500">
        <Trans>Chargement du dossier...</Trans>
      </p>
    );
  }

  if (!dossier) {
    return (
      <div className="p-6">
        <BackLink to={backTo} state={origin?.state} />
        <p className="mt-4 text-sm text-slate-600">
          <Trans>Ce dossier est introuvable ou ne relève pas de votre clinique.</Trans>
        </p>
      </div>
    );
  }

  const name = dossierName(dossier);

  return (
    <div className="p-6">
      <BackLink to={backTo} state={origin?.state} />

      {/* --- who and how urgent, once, at the top ------------------------------------- */}
      <header className="mb-5 mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-200 pb-4">
        <h1 className="mr-2 text-xl font-semibold text-slate-900">
          {name ?? <Trans>Patient non identifié</Trans>}
        </h1>
        {/* Same rule as the queue: once the avis is rendered, the marks keep their words and
            lose their hues. A dossier that shouted on one screen and not on the other is a dossier
            the reader has to look at twice to believe. */}
        <span
          className={cn(
            TAG,
            priorityStyle(dossier),
          )}
        >
          {dossier.priorite_libelle ?? dossier.priorite_proposee ?? t`Sans priorité`}
        </span>
        {dossier.urgence_clinique && (
          <span
            className={cn(
              TAG,
              delaiStyle(dossier),
            )}
            title={t`Au moins un signal fort fixe un délai — pas seulement une intention`}
          >
            <TimerReset className="h-3 w-3" />
            <Trans>Délai clinique</Trans>
          </span>
        )}
        {dossier.mineur && (
          <span className={cn(TAG, "bg-slate-200 text-slate-700")}>
            <Baby className="h-3 w-3" />
            <Trans>Mineur</Trans>
          </span>
        )}
        <span
          className={cn(
            TAG,
            statutStyle(dossier.triage_statut),
          )}
        >
          {i18n._(STATUT_LABELS[dossier.triage_statut])}
        </span>
        {/* The one way into the record, beside the one place the person is named. When there is
            no record yet, say so here rather than leave the reader to notice a missing link — the
            identity cascade takes minutes, and « not yet » is an answer. */}
        {dossier.patient ? (
          <Link
            to={`/patient/${dossier.patient.tenant_id}/${dossier.patient.id_patient}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            <UserRound className="h-3.5 w-3.5" />
            <Trans>Dossier patient</Trans>
            <ExternalLink className="h-3 w-3" />
          </Link>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-slate-400">
            <UserRound className="h-3.5 w-3.5" />
            <Trans>Patient non encore rattaché</Trans>
          </span>
        )}
      </header>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,460px)]">
        <div className="order-2 xl:order-1">
          <DossierDetail
            dossier={dossier}
            onDownload={downloadPiece}
            hrefForDossier={(dossierId) => `/tele-expertise/${dossierId}`}
          />
        </div>
        <div className="order-1 xl:order-2 xl:sticky xl:top-6 xl:self-start">
          <CompteRenduPanel
            compteRendu={dossier.compte_rendu}
            suiteDonnee={dossier.suite_donnee}
            statut={dossier.triage_statut}
            busy={busy}
            onGenerate={(suite) => void generate(suite)}
            onSave={(texte) => void save(texte)}
            onValidate={(suite, texte, internalNote) => void validate(suite, texte, internalNote)}
            onDownload={(brouillon) => void download(brouillon)}
            onReopen={() => void reopen()}
            note={note}
            onNoteChange={setNote}
          />
        </div>
      </div>
    </div>
  );
}

function BackLink({ to, state }: { to: To; state?: unknown }) {
  return (
    <Link
      to={to}
      state={state}
      className="inline-flex items-center gap-1.5 text-sm text-slate-600 transition-colors hover:text-primary"
    >
      <ArrowLeft className="h-4 w-4" />
      <Trans>Retour à la file d'attente</Trans>
    </Link>
  );
}
